import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect, FormSectionTitle } from '@practium/ui';
import type { SystemPageMode, HierarchyFormData } from '../../../types';
import { useHierarchyForm } from '../../../hooks/useHierarchyForm';
import styles from '../SystemForm.module.css';

export interface HierarchyFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface HierarchyFormProps {
	id?: string;
	mode: SystemPageMode;
	initialData?: HierarchyFormData;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
}

export const HierarchyForm = forwardRef<HierarchyFormRef, HierarchyFormProps>(
	function HierarchyForm({ id, mode, initialData, onSubmit, onLoadingChange }, ref) {
		const { form, groupOptions, errors, isLoading, set, handleSubmit, isDirty } =
			useHierarchyForm({ id, initialData, onSubmit });

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';

		if (isView) {
			return (
				<div className={styles.wrapper}>
					<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
					<div className={styles.viewGrid}>
						<div className={`${styles.viewField} ${styles.fullWidth}`}>
							<span className={styles.viewLabel}>Nome</span>
							<span className={styles.viewValue}>{form.name || '—'}</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Nível 1</span>
							<span className={styles.viewValue}>
								{groupOptions.find(o => o.value === form.farmer)?.label || form.farmer || '—'}
							</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Nível 2</span>
							<span className={styles.viewValue}>
								{groupOptions.find(o => o.value === form.consultant)?.label || form.consultant || '—'}
							</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Nível 3</span>
							<span className={styles.viewValue}>
								{groupOptions.find(o => o.value === form.company)?.label || form.company || '—'}
							</span>
						</div>
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Nível 4</span>
							<span className={styles.viewValue}>
								{groupOptions.find(o => o.value === form.master)?.label || form.master || '—'}
							</span>
						</div>
						{form.description && (
							<div className={`${styles.viewField} ${styles.fullWidth}`}>
								<span className={styles.viewLabel}>Observações</span>
								<span className={styles.viewValue}>{form.description}</span>
							</div>
						)}
					</div>
				</div>
			);
		}

		const anyLevelFilled = !!(form.farmer || form.consultant || form.company || form.master);

		return (
			<div className={styles.wrapper}>
				<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
				<div className={styles.formGrid}>
					<div className={styles.fullWidth}>
						<FormField label="Nome" required error={errors.name}>
							<FormInput
								value={form.name}
								onChange={e => set('name', e.target.value)}
								placeholder="Ex.: Sub-admin"
								hasError={!!errors.name}
							/>
						</FormField>
					</div>

					<FormField label="Nível 1" required={!anyLevelFilled} error={errors.farmer}>
						<FormSelect
							value={form.farmer}
							onChange={v => set('farmer', v)}
							options={groupOptions}
							placeholder="Selecione o nível"
							clearable
							hasError={!!errors.farmer}
						/>
					</FormField>
					<FormField label="Nível 2" required={!anyLevelFilled}>
						<FormSelect
							value={form.consultant}
							onChange={v => set('consultant', v)}
							options={groupOptions}
							placeholder="Selecione o nível"
							clearable
						/>
					</FormField>
					<FormField label="Nível 3" required={!anyLevelFilled}>
						<FormSelect
							value={form.company}
							onChange={v => set('company', v)}
							options={groupOptions}
							placeholder="Selecione o nível"
							clearable
						/>
					</FormField>
					<FormField label="Nível 4" required={!anyLevelFilled}>
						<FormSelect
							value={form.master}
							onChange={v => set('master', v)}
							options={groupOptions}
							placeholder="Selecione o nível"
							clearable
						/>
					</FormField>

					<div className={styles.fullWidth}>
						<FormField label="Observações">
							<textarea
								className={styles.textarea}
								value={form.description}
								onChange={e => set('description', e.target.value)}
								placeholder="Ex.: Essa hierarquia é feita para..."
								rows={8}
							/>
						</FormField>
					</div>
				</div>
			</div>
		);
	}
);
